import { MouseEvent } from 'react';

import { Chip, CircularProgress, Tooltip } from '@mui/material';

import first from 'lodash/first';
import orderBy from 'lodash/orderBy';

import AlertTriangleIcon from '@/components/Icons/AlertTriangleIcon';
import DotIcon from '@/components/Icons/DotIcon';
import MoonIcon from '@/components/Icons/MoonIcon';
import {
  ChainDeploymentByStatusCount,
  ChainDeploymentStatus,
  DeploymentStatus,
  OracleProductionReleaseStatus,
  TrainingJobStatus,
  TransportKind,
} from '@/graphql';
import {
  ChainDeploymentFragment,
  ChainStableEnvironmentFragment,
  ChainletMetaFragment,
  ChainletOracleVersionFragment,
} from '@/pages/Chain/__generated__/fragments.generated';
import { StableEnvironmentFragment } from '@/pages/Model/__generated__/fragments.generated';
import { TrainingJobFragment } from '@/sections/Training/__generated__/fragments.generated';
import { ColorScale } from '@/theme/colors';
import { pluralizeString } from '@/utils/pluralizeString';
import { ApolloError } from '@apollo/client';

import {
  ModelVersionCountByStatusFragment,
  OverviewVersionFragment,
  OverviewVersionMetaFragment,
  ProductionReleasesForChainEnvironmentFragment,
  ProgressProductionReleaseFragment,
} from './__generated__/fragments.generated';
import { Entity, FilterMode, PromotionDetails, TableDeploymentStatus } from './types';

const PROD_ENV_NAME = 'production';

function getProductionVersionMeta(versions: OverviewVersionMetaFragment[] = []) {
  return versions.find((version) => version.isPrimary);
}

function getDevelopmentVersionMeta(versions: OverviewVersionMetaFragment[] = []) {
  return versions.find((version) => version.isDraft);
}

function getProductionChainDeployment(deployments: ChainDeploymentFragment[] = []) {
  return deployments.find((deployment) => deployment.isPrimary);
}

function getDevelopmentChainDeployment(deployments: ChainDeploymentFragment[] = []) {
  return deployments.find((deployment) => deployment.isDraft);
}

function getDefaultVersionId(versions: OverviewVersionMetaFragment[] = []) {
  const prodVersionMeta = getProductionVersionMeta(versions);
  const devVersionMeta = getDevelopmentVersionMeta(versions);

  return prodVersionMeta?.id || devVersionMeta?.id || first(versions)?.id;
}

function getDefaultDeployment(deployments: ChainDeploymentFragment[] = []) {
  const prodDeployment = deployments.find((deployment) => deployment.isPrimary);
  const devDeployment = deployments.find((deployment) => deployment.isDraft);

  return prodDeployment || devDeployment || first(deployments);
}

function canVersionBePromotedOld(versionMeta: OverviewVersionMetaFragment) {
  return (
    !versionMeta.isPrimary &&
    [
      DeploymentStatus.ModelReady,
      DeploymentStatus.ScaledToZero,
      DeploymentStatus.ScalingFromZero,
    ].includes(versionMeta.currentModelDeploymentStatus.status)
  );
}

function canVersionBePromoted(
  versionMeta: OverviewVersionMetaFragment,
  envs: StableEnvironmentFragment[] = [],
  hasAccessToProdEnv = true,
) {
  if (versionMeta.isPrimary || !hasAccessToProdEnv) {
    return !!envs.length;
  }

  return [
    DeploymentStatus.ModelReady,
    DeploymentStatus.ScaledToZero,
    DeploymentStatus.ScalingFromZero,
  ].includes(versionMeta.currentModelDeploymentStatus.status);
}

function checkIfOffline(error?: ApolloError) {
  return error?.message === 'Failed to fetch' && !window.navigator.onLine;
}

function getDeploymentAddress(chainDeployment: ChainDeploymentFragment, envName?: string) {
  if (chainDeployment.isPrimary) {
    return `environments/${PROD_ENV_NAME}`;
  }

  if (chainDeployment.isDraft) {
    return 'development';
  }

  if (envName) {
    return `environments/${envName}`;
  }

  return `deployment/${chainDeployment.id}`;
}

function getChainletUrl(
  chainDeployment: ChainDeploymentFragment,
  chainlet: ChainletMetaFragment,
  path: string,
  chainHostname: string,
): string {
  let { invokeBaseUrl: baseUrl } = chainlet;
  if (path === 'wake') {
    baseUrl = chainHostname;
  }
  return `${baseUrl}/deployment/${chainDeployment.id}/chainlet/${chainlet.id}/${path}`;
}

function getChainUrl(
  chainDeployment: ChainDeploymentFragment,
  isAsync: boolean,
  chainHostname: string,
  transportKind?: TransportKind,
  envName?: string,
  chainlet?: ChainletMetaFragment,
): string {
  if (chainlet && !chainlet.isEntrypoint) {
    return getChainletUrl(chainDeployment, chainlet, 'run_remote', chainHostname);
  }

  let suffix = isAsync ? 'async_run_remote' : 'run_remote';
  if (transportKind === TransportKind.Websocket) {
    suffix = 'websocket';
  }

  return `${chainDeployment.invokeBaseUrl}/${getDeploymentAddress(
    chainDeployment,
    envName,
  )}/${suffix}`;
}

function getModelVersionUrl(
  version: Partial<OverviewVersionFragment> | Partial<ChainletOracleVersionFragment>,
  modelPath = 'predict',
  env?: StableEnvironmentFragment,
  transportKind?: TransportKind,
): string | null {
  if (!version) return null;

  let baseUrl = version.invokeBaseUrl;
  if (modelPath === 'wake') {
    baseUrl = version.wakeBaseUrl;
  }
  const url = `${baseUrl}${transportKind === TransportKind.Grpc ? '' : `/${modelPath}`}`;

  if (version.isPrimary) {
    return url.replace('production', 'environments/production');
  }

  if (env) {
    return url.replace(`deployment/${version.id}`, `environments/${env.name}`);
  }

  return url;
}

const mapDeploymentStatusToFilter: Record<DeploymentStatus | ChainDeploymentStatus, FilterMode> = {
  [DeploymentStatus.BuildingModel]: FilterMode.RUNNING,
  [DeploymentStatus.BuildingModelFailed]: FilterMode.FAILED,
  [DeploymentStatus.BuildingModelStopped]: FilterMode.INACTIVE,
  [DeploymentStatus.DeactivatedModel]: FilterMode.INACTIVE,
  [DeploymentStatus.DeactivatingModel]: FilterMode.RUNNING,
  [DeploymentStatus.DeployingModel]: FilterMode.RUNNING,
  [DeploymentStatus.MigratingWorkloadPlanes]: FilterMode.RUNNING,
  [DeploymentStatus.ModelDeployFailed]: FilterMode.FAILED,
  [DeploymentStatus.ModelDneError]: FilterMode.FAILED,
  [DeploymentStatus.ModelLoading]: FilterMode.RUNNING,
  [DeploymentStatus.ModelReady]: FilterMode.RUNNING,
  [DeploymentStatus.ModelUnhealthy]: FilterMode.RUNNING,
  [DeploymentStatus.ScaledToZero]: FilterMode.SCALED_TO_ZERO,
  [DeploymentStatus.ScalingFromZero]: FilterMode.RUNNING,
  [DeploymentStatus.Updating]: FilterMode.RUNNING,
};

const mapDeploymentStatusToModelTableDeploymentStatus: Record<
  DeploymentStatus,
  TableDeploymentStatus
> = {
  [DeploymentStatus.ModelReady]: TableDeploymentStatus.Running,
  [DeploymentStatus.ScalingFromZero]: TableDeploymentStatus.InProgress,
  [DeploymentStatus.MigratingWorkloadPlanes]: TableDeploymentStatus.InProgress,
  [DeploymentStatus.BuildingModel]: TableDeploymentStatus.InProgress,
  [DeploymentStatus.Updating]: TableDeploymentStatus.InProgress,
  [DeploymentStatus.DeployingModel]: TableDeploymentStatus.InProgress,
  [DeploymentStatus.ModelLoading]: TableDeploymentStatus.InProgress,
  [DeploymentStatus.ScaledToZero]: TableDeploymentStatus.ScaledToZero,
  [DeploymentStatus.ModelUnhealthy]: TableDeploymentStatus.Unhealthy,
  [DeploymentStatus.DeactivatingModel]: TableDeploymentStatus.Other,
  [DeploymentStatus.BuildingModelFailed]: TableDeploymentStatus.Failed,
  [DeploymentStatus.ModelDeployFailed]: TableDeploymentStatus.Failed,
  [DeploymentStatus.BuildingModelStopped]: TableDeploymentStatus.Other,
  [DeploymentStatus.DeactivatedModel]: TableDeploymentStatus.Other,
  [DeploymentStatus.ModelDneError]: TableDeploymentStatus.Other,
};

const mapTrainingJobStatusToTableDeploymentStatus: Record<
  TrainingJobStatus,
  TableDeploymentStatus
> = {
  [TrainingJobStatus.TrainingJobCreated]: TableDeploymentStatus.InProgress,
  [TrainingJobStatus.TrainingJobDeploying]: TableDeploymentStatus.InProgress,
  [TrainingJobStatus.TrainingJobQueued]: TableDeploymentStatus.InProgress,
  [TrainingJobStatus.TrainingJobDeployFailed]: TableDeploymentStatus.Unhealthy,
  [TrainingJobStatus.TrainingJobRunning]: TableDeploymentStatus.Running,
  [TrainingJobStatus.TrainingJobStopped]: TableDeploymentStatus.Other,
  [TrainingJobStatus.TrainingJobCompleted]: TableDeploymentStatus.Other,
  [TrainingJobStatus.TrainingJobFailed]: TableDeploymentStatus.Failed,
};

const iconForStatus: Record<TableDeploymentStatus, JSX.Element> = {
  [TableDeploymentStatus.Running]: <DotIcon color="success" />,
  [TableDeploymentStatus.InProgress]: <CircularProgress size={10} />,
  [TableDeploymentStatus.ScaledToZero]: <MoonIcon color="secondary" />,
  [TableDeploymentStatus.Unhealthy]: <AlertTriangleIcon sx={{ color: 'text.warn' }} />,
  [TableDeploymentStatus.Other]: null,
  [TableDeploymentStatus.Failed]: null,
};

const colorForStatus: Record<TableDeploymentStatus, string> = {
  [TableDeploymentStatus.Running]: ColorScale.green[600],
  [TableDeploymentStatus.InProgress]: ColorScale.blue[500],
  [TableDeploymentStatus.ScaledToZero]: ColorScale.gray[200],
  [TableDeploymentStatus.Unhealthy]: ColorScale.yellow[500],
  [TableDeploymentStatus.Other]: ColorScale.gray[300],
  [TableDeploymentStatus.Failed]: ColorScale.red[500],
};

const TableDeploymentStatusOrder = [
  TableDeploymentStatus.Running,
  TableDeploymentStatus.InProgress,
  TableDeploymentStatus.ScaledToZero,
  TableDeploymentStatus.Unhealthy,
  TableDeploymentStatus.Other,
  TableDeploymentStatus.Failed,
];

const CAN_DEACTIVATE_STATES = [
  DeploymentStatus.ModelReady,
  DeploymentStatus.ScaledToZero,
  DeploymentStatus.ScalingFromZero,
  DeploymentStatus.Updating,
  DeploymentStatus.MigratingWorkloadPlanes,
  DeploymentStatus.ModelUnhealthy,
  DeploymentStatus.ModelDeployFailed,
  DeploymentStatus.ModelLoading,
];

const CAN_PROMOTE_STATES = [
  DeploymentStatus.ModelReady,
  DeploymentStatus.ScaledToZero,
  DeploymentStatus.ScalingFromZero,
];

function canDeploymentBeDeactivated(status: DeploymentStatus) {
  return CAN_DEACTIVATE_STATES.includes(status);
}

function canDeploymentBePromoted(
  deployment: ChainDeploymentFragment,
  envs: ChainStableEnvironmentFragment[],
  hasAccessToProdEnv = true,
) {
  const canPromote = CAN_PROMOTE_STATES.includes(deployment.status as unknown as DeploymentStatus);

  if (deployment.isPrimary || !hasAccessToProdEnv) {
    return canPromote && !!envs.length;
  }

  return canPromote;
}

function getVersionsByStatus(
  versionCountByStatus: Array<ModelVersionCountByStatusFragment | ChainDeploymentByStatusCount>,
  initial: Record<TableDeploymentStatus, number> = {} as Record<TableDeploymentStatus, number>,
  includeFailedInOther = false,
): Record<TableDeploymentStatus, number> {
  const countByStatus = versionCountByStatus.reduce((acc, { status, count }) => {
    let modelStatus = mapDeploymentStatusToModelTableDeploymentStatus[status];

    if (includeFailedInOther && modelStatus === TableDeploymentStatus.Failed) {
      modelStatus = TableDeploymentStatus.Other;
    }

    if (!acc[modelStatus]) {
      acc[modelStatus] = 0;
    }

    acc[modelStatus] += count;

    return acc;
  }, initial);

  if (!countByStatus.Running) {
    countByStatus.Running = 0;
  }
  if (!countByStatus.ScaledToZero) {
    countByStatus.ScaledToZero = 0;
  }

  return countByStatus;
}

function getTrainingJobsByStatus(
  trainingJobs: TrainingJobFragment[],
  initial: Record<TableDeploymentStatus, number> = {} as Record<TableDeploymentStatus, number>,
): Record<TableDeploymentStatus, number> {
  const countByStatus = trainingJobs.reduce((acc, { status }) => {
    const trainingJobStatus = mapTrainingJobStatusToTableDeploymentStatus[status.value];
    if (!acc[trainingJobStatus]) {
      acc[trainingJobStatus] = 0;
    }

    acc[trainingJobStatus] += 1;

    return acc;
  }, initial);

  if (!countByStatus.Running) {
    countByStatus.Running = 0;
  }

  return countByStatus;
}
function sortVersionsByStatus(
  versionsByStatus: Record<TableDeploymentStatus, number>,
): [TableDeploymentStatus, number][] {
  return orderBy(
    Object.entries(versionsByStatus) as [TableDeploymentStatus, number][],
    ([status]) => TableDeploymentStatusOrder.indexOf(status),
  );
}

function getEntityDeploymentsCount(entity: Entity): number {
  if (
    // eslint-disable-next-line no-underscore-dangle
    entity.__typename === 'OrganizationSharedEndpointType'
  ) {
    return 1;
  }

  const countByStatus =
    // eslint-disable-next-line no-underscore-dangle
    entity.__typename === 'ModelType'
      ? entity.versionCountByStatus
      : entity.deploymentCountByStatus;

  return [...countByStatus].reduce(
    (acc: number, { count }: ModelVersionCountByStatusFragment | ChainDeploymentByStatusCount) =>
      acc + count,
    0,
  );
}

// if it's less than 60 seconds, we want to show seconds
// if it's less than 60 minutes, we want to show minutes + seconds (if remaining seconds > 0)
// if it's less than 24 hours, we want to show hours + minutes (if remaining minutes > 0) + seconds (if remaining seconds > 0)
// if it's more than 24 hours, we want to show days + hours (if remaining hours > 0) + minutes (if remaining minutes > 0) + seconds (if remaining seconds > 0)
function formatSeconds(seconds: number): string {
  const timeUnits = [
    { unit: 'day', seconds: 86400 },
    { unit: 'hour', seconds: 3600 },
    { unit: 'minute', seconds: 60 },
    { unit: 'second', seconds: 1 },
  ];

  for (const { unit, seconds: unitSeconds } of timeUnits) {
    if (seconds >= unitSeconds) {
      const unitCount = Math.floor(seconds / unitSeconds);
      const remainingSeconds = seconds % unitSeconds;
      const unitString = pluralizeString(unitCount, unit);
      return `${unitString}${remainingSeconds > 0 ? ` ${formatSeconds(remainingSeconds)}` : ''}`;
    }
  }

  return pluralizeString(seconds, 'second');
}

const MAX_ENV_NAME_LENGTH = 50;
// env name should only contain lowercase alphanumeric characters
const ENV_NAME_REGEX = /^[a-z0-9]+$/;

function getEnvNameErrors(name: string): string[] {
  if (!name) return [];

  const errors = [];

  if (name === PROD_ENV_NAME || name === 'development') {
    errors.push('Environment name is reserved');
  }

  if (name.length > MAX_ENV_NAME_LENGTH) {
    errors.push('Environment name should be less than 50 characters');
  }

  if (!ENV_NAME_REGEX.test(name)) {
    errors.push('Environment name should only contain lowercase alphanumeric characters');
  }

  return errors;
}

function promotionDetailsFromRelease(
  progressProductionRelease:
    | ProgressProductionReleaseFragment
    | ProductionReleasesForChainEnvironmentFragment,
): PromotionDetails | null {
  if (!progressProductionRelease) {
    return null;
  }

  const isRampingUp = progressProductionRelease.status === OracleProductionReleaseStatus.RampingUp;
  return isRampingUp
    ? {
        percentageTraffic: progressProductionRelease.percentTrafficToNewVersion,
        rampUpDurationSeconds: progressProductionRelease.rampUpDurationSeconds,
        rollingDeploy: progressProductionRelease.rollingDeploy,
        rollingDeployTargetScale: progressProductionRelease.rollingDeployTargetScale,
        rampUpStartedAt: progressProductionRelease.rampUpStartedAt
          ? new Date(progressProductionRelease.rampUpStartedAt)
          : null,
      }
    : null;
}

function calculateTimeRemaining(promotionDetails: PromotionDetails): number {
  const secondsElapsed = Math.floor(
    (new Date().getTime() - promotionDetails.rampUpStartedAt?.getTime()) / 1000,
  );
  return promotionDetails.rampUpDurationSeconds - secondsElapsed;
}

function checkIfOpenAiCompatible(version: OverviewVersionFragment): boolean {
  return version?.clientCompatibility?.isOpenaiCompatible;
}

function checkIfBasetenPerformanceClientCompatible(version: OverviewVersionFragment): boolean {
  return version?.clientCompatibility?.isBasetenPerfClientCompatible;
}

function samePageLinkNavigation(ev: MouseEvent<HTMLAnchorElement>) {
  if (
    ev.defaultPrevented ||
    ev.button !== 0 || // ignore everything but left-click
    ev.metaKey ||
    ev.ctrlKey ||
    ev.altKey ||
    ev.shiftKey
  ) {
    return false;
  }

  return true;
}

function preventDefault(ev: MouseEvent<HTMLAnchorElement>) {
  if (samePageLinkNavigation(ev)) {
    ev.preventDefault();
  }
}

function getTransportKindBadge(transportKind: TransportKind) {
  if (!transportKind || transportKind === TransportKind.Http) return null;

  const getTransportKindLabel = (kind: TransportKind) => {
    switch (kind) {
      case TransportKind.Grpc:
        return 'gRPC';
      case TransportKind.Websocket:
        return 'WebSocket';
    }
  };

  return (
    <Tooltip title={`Invoke via ${getTransportKindLabel(transportKind)}`}>
      <Chip label={getTransportKindLabel(transportKind)} size="small" color="secondary" />
    </Tooltip>
  );
}

function getChainDeploymentDisplayName(name: string | null | undefined): string {
  return name || '--';
}

function getChainDeploymentNameForRename(name: string | null | undefined): string {
  return name ?? '';
}

export {
  checkIfOffline,
  checkIfOpenAiCompatible,
  checkIfBasetenPerformanceClientCompatible,
  getModelVersionUrl,
  canVersionBePromoted,
  getDefaultVersionId,
  getProductionVersionMeta,
  getDevelopmentVersionMeta,
  canDeploymentBePromoted,
  mapDeploymentStatusToFilter,
  getEntityDeploymentsCount,
  mapDeploymentStatusToModelTableDeploymentStatus,
  TableDeploymentStatusOrder,
  iconForStatus,
  getVersionsByStatus,
  colorForStatus,
  canDeploymentBeDeactivated,
  formatSeconds,
  getDefaultDeployment,
  getProductionChainDeployment,
  getDevelopmentChainDeployment,
  getChainletUrl,
  getChainUrl,
  canVersionBePromotedOld,
  PROD_ENV_NAME,
  getEnvNameErrors,
  promotionDetailsFromRelease,
  calculateTimeRemaining,
  preventDefault,
  samePageLinkNavigation,
  sortVersionsByStatus,
  mapTrainingJobStatusToTableDeploymentStatus,
  getTrainingJobsByStatus,
  getTransportKindBadge,
  getChainDeploymentDisplayName,
  getChainDeploymentNameForRename,
};
